import api from '../hooks/useHttp';

const SIGNED_URL_CACHE_TTL_MS = 30 * 60 * 1000;

const singleUrlCache = new Map();
const batchUrlCache = new Map();

function getCachedSingle(cacheKey) {
  const entry = singleUrlCache.get(cacheKey);
  if (!entry || Date.now() - entry.at > SIGNED_URL_CACHE_TTL_MS) return null;
  return entry.url;
}

function setCachedSingle(cacheKey, url) {
  singleUrlCache.set(cacheKey, { url, at: Date.now() });
}

function getCachedBatch(cacheKey) {
  const entry = batchUrlCache.get(cacheKey);
  if (!entry || Date.now() - entry.at > SIGNED_URL_CACHE_TTL_MS) return null;
  return entry.urls;
}

function setCachedBatch(cacheKey, urls) {
  batchUrlCache.set(cacheKey, { urls, at: Date.now() });
}

class FileService {
  async getSignedUrl(key, expiresIn = 3600) {
    if (!key) return null;

    if (key.startsWith('http://') || key.startsWith('https://') || key.startsWith('blob:')) {
      return key;
    }

    const cacheKey = `${key}:${expiresIn}`;
    const cached = getCachedSingle(cacheKey);
    if (cached) return cached;

    try {
      const encodedKey = encodeURIComponent(key);
      const response = await api.get(`/files/signed-url/${encodedKey}`, {
        params: { expiresIn }
      });

      const url = response.data?.url || key;
      setCachedSingle(cacheKey, url);
      return url;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return null;
    }
  }

  async getSignedUrls(keys, expiresIn = 3600) {
    if (!Array.isArray(keys) || keys.length === 0) return {};

    const keysToFetch = keys.filter(
      (k) =>
        k &&
        !k.startsWith('http://') &&
        !k.startsWith('https://') &&
        !k.startsWith('blob:')
    );

    if (keysToFetch.length === 0) {
      const urlMap = {};
      keys.forEach((k) => {
        if (k) urlMap[k] = k;
      });
      return urlMap;
    }

    const cacheKey = `${keysToFetch.slice().sort().join('|')}:${expiresIn}`;
    const cached = getCachedBatch(cacheKey);
    if (cached) {
      const urlMap = { ...cached };
      keys.forEach((k) => {
        if (k && (k.startsWith('http://') || k.startsWith('https://') || k.startsWith('blob:')))
          urlMap[k] = k;
      });
      return urlMap;
    }

    try {
      const response = await api.post('/files/signed-urls', {
        keys: keysToFetch,
        expiresIn
      });

      const urlMap = response.data?.urls || {};

      keys.forEach((k) => {
        if (k && (k.startsWith('http://') || k.startsWith('https://') || k.startsWith('blob:')))
          urlMap[k] = k;
      });

      const toCache = {};
      keysToFetch.forEach((k) => {
        if (urlMap[k]) toCache[k] = urlMap[k];
      });
      setCachedBatch(cacheKey, toCache);
      return urlMap;
    } catch (error) {
      console.error('Error getting signed URLs:', error);
      const urlMap = {};
      keys.forEach((k) => {
        if (k) urlMap[k] = k;
      });
      return urlMap;
    }
  }

  /**
   * Convert array of S3 keys to signed URLs
   * @param {Array<String>} keys - Array of S3 keys or URLs
   * @param {Number} expiresIn - Expiration time in seconds
   * @returns {Promise<Array<String>>} - Array of signed URLs
   */
  async convertKeysToSignedUrls(keys, expiresIn = 3600) {
    if (!Array.isArray(keys) || keys.length === 0) return [];

    const urlMap = await this.getSignedUrls(keys, expiresIn);
    return keys.map(key => urlMap[key] || key).filter(url => url);
  }
}

export default new FileService();




















